/**
 * Конфиг подсказок страниц для PageHelpPopover.
 * Для каждой страницы задаётся ключ перевода описания и ссылка на документацию.
 *
 * При добавлении новой страницы:
 * 1. Добавить ключ перевода описания (subtitle) в locales (ru.json, en.json).
 * 2. Добавить запись в PAGE_HELP_CONFIG с ключом страницы.
 * 3. На странице использовать <PageHelpPopover pageKey="ключ" /> рядом с заголовком.
 *
 * Ссылки ведут только на публичные страницы портала документации. Админские
 * экраны указывают на корень: внутренние разделы портала видны лишь роли admin,
 * и их адреса не должны попадать в бандл, который отдаётся всем.
 */

const DOCS_BASE = 'https://docs.easya.ru/'

/** Адрес страницы портала документации: page('teacher/cards') → https://docs.easya.ru/teacher/cards */
const page = (path: string): string => `${DOCS_BASE}${path}`

export interface PageHelpConfigItem {
  /** Ключ перевода описания страницы (i18n, например dashboard.subtitle) */
  descriptionKey: string
  /** Ссылка на документацию для этой страницы (URL или путь относительно DOCS_BASE) */
  docsUrl: string
}

/** Ключ страницы — логический идентификатор (совпадает с путём или именем раздела) */
export type PageHelpKey = keyof typeof PAGE_HELP_CONFIG

export const PAGE_HELP_CONFIG = {
  'admin-programs': {
    descriptionKey: 'admin.programs.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-placement-bank': {
    descriptionKey: 'admin.placementBank.subtitle',
    docsUrl: DOCS_BASE,
  },
  programs: {
    descriptionKey: 'programs.subtitle',
    docsUrl: page('teacher/learning-programs'),
  },
  'program-assignment': {
    descriptionKey: 'programs.assignment.subtitle',
    docsUrl: page('teacher/learning-programs'),
  },
  'my-programs': {
    descriptionKey: 'programs.my.subtitle',
    docsUrl: page('student/homework-programs'),
  },
  'user-programs': {
    descriptionKey: 'programs.userSubtitle',
    docsUrl: page('teacher/learning-programs'),
  },
  'exercise-run': {
    descriptionKey: 'exercises.run.subtitle',
    docsUrl: page('student/exercises'),
  },
  'exercise-bank': {
    descriptionKey: 'exercises.bank.subtitle',
    docsUrl: page('teacher/exercises'),
  },
  'exercise-review': {
    descriptionKey: 'exercises.review.subtitle',
    docsUrl: page('teacher/exercise-review'),
  },
  'homework-item-check': {
    descriptionKey: 'homeworks.itemCheck.help',
    docsUrl: page('teacher/exercise-review'),
  },
  dashboard: {
    descriptionKey: 'dashboard.subtitle',
    docsUrl: page('common/dashboard'),
  },
  activity: {
    descriptionKey: 'activity.subtitle',
    docsUrl: page('teacher/activity'),
  },
  // Ученические пары к учительским ключам — тот же приём, что у tests /
  // user-tests и lessons / lessons-student. Без них ученик со своей страницы
  // попадал на статью с заголовком «(для учителя)», хотя ученическая версия
  // в портале есть и не использовалась ни одной подсказкой.
  'activity-student': {
    descriptionKey: 'activity.subtitle',
    docsUrl: page('student/activity'),
  },
  lessons: {
    descriptionKey: 'lessons.subtitle',
    docsUrl: page('teacher/lesson-schedules'),
  },
  'lessons-student': {
    descriptionKey: 'lessons.studentSubtitle',
    docsUrl: page('student/lessons'),
  },
  'lesson-schedules': {
    descriptionKey: 'lessonSchedules.studentPageSubtitle',
    docsUrl: page('teacher/lesson-schedules'),
  },
  homeworks: {
    descriptionKey: 'homeworks.subtitle',
    docsUrl: page('teacher/homework'),
  },
  profile: {
    descriptionKey: 'profile.subtitle',
    docsUrl: page('common/profile-page'),
  },
  'profile-connections': {
    descriptionKey: 'mcpConnections.subtitle',
    docsUrl: page('teacher/ai-agent-mcp'),
  },
  'profile-security': {
    descriptionKey: 'profile.security.subtitle',
    docsUrl: page('common/profile-security'),
  },
  'profile-student-form-fields': {
    descriptionKey: 'studentForms.fieldsSubtitle',
    docsUrl: page('teacher/student-form-fields'),
  },
  'admin-student-form-fields': {
    descriptionKey: 'studentForms.catalogSubtitle',
    docsUrl: DOCS_BASE,
  },
  'profile-subscription': {
    descriptionKey: 'subscription.subtitle',
    docsUrl: page('teacher/subscriptions-overview'),
  },
  'profile-balance': {
    descriptionKey: 'balance.subtitle',
    docsUrl: page('teacher/balance'),
  },
  'tts-tasks': {
    descriptionKey: 'tts.tasksSubtitle',
    docsUrl: page('teacher/tts'),
  },
  'admin-topup-requests': {
    descriptionKey: 'admin.topupRequests.subtitle',
    docsUrl: DOCS_BASE,
  },
  'profile-billing': {
    descriptionKey: 'billing.teacherPageSubtitle',
    docsUrl: page('teacher/subscriptions-payments'),
  },
  'student-finance': {
    descriptionKey: 'billing.studentFinanceSubtitle',
    docsUrl: page('teacher/lesson-subscriptions'),
  },
  'user-finance': {
    descriptionKey: 'billing.userFinanceSubtitle',
    docsUrl: page('teacher/contracts'),
  },
  'profile-plans': {
    descriptionKey: 'subscription.plansSubtitle',
    docsUrl: page('teacher/subscriptions-change-plan'),
  },
  groups: {
    descriptionKey: 'groups.subtitle',
    docsUrl: page('teacher/groups'),
  },
  words: {
    descriptionKey: 'words.subtitle',
    docsUrl: page('teacher/words-edit'),
  },
  users: {
    descriptionKey: 'users.subtitle',
    docsUrl: page('teacher/contracts'),
  },
  dictionaries: {
    descriptionKey: 'dictionaries.subtitle',
    docsUrl: page('teacher/dictionaries'),
  },
  tags: {
    descriptionKey: 'tags.subtitle',
    docsUrl: page('teacher/tags'),
  },
  cards: {
    descriptionKey: 'cards.subtitle',
    docsUrl: page('teacher/cards'),
  },
  'cards-student': {
    descriptionKey: 'cards.subtitle',
    docsUrl: page('student/cards'),
  },
  // Просмотр одной карточки — своя статья у каждой роли: страница списка
  // описывает не то, что видит человек, открывший карточку.
  'card-view': {
    descriptionKey: 'cards.subtitle',
    docsUrl: page('teacher/card-view'),
  },
  'card-view-student': {
    descriptionKey: 'cards.subtitle',
    docsUrl: page('student/card-view'),
  },
  teacher: {
    descriptionKey: 'teacherPage.subtitle',
    docsUrl: page('student/my-teacher'),
  },
  'admin-users': {
    descriptionKey: 'admin.users.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-payments': {
    descriptionKey: 'admin.payments.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-finance': {
    descriptionKey: 'admin.finance.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-subscriptions': {
    descriptionKey: 'admin.subscriptions.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-plans': {
    descriptionKey: 'admin.plans.subtitle',
    docsUrl: DOCS_BASE,
  },
  news: {
    descriptionKey: 'news.subtitle',
    docsUrl: page('common/news'),
  },
  'admin-news': {
    descriptionKey: 'admin.news.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-chat-stickers': {
    descriptionKey: 'admin.chatStickers.subtitle',
    docsUrl: DOCS_BASE,
  },
  'admin-chat-sticker-pack': {
    descriptionKey: 'admin.chatStickers.packSubtitle',
    docsUrl: DOCS_BASE,
  },
  tests: {
    descriptionKey: 'tests.subtitle',
    docsUrl: page('student/tests'),
  },
  'user-tests': {
    descriptionKey: 'tests.userSubtitle',
    docsUrl: page('teacher/tests'),
  },
  'tests-self-create': {
    descriptionKey: 'tests.selfCreateSubtitle',
    docsUrl: page('student/tests#self-test-settings'),
  },
  'dictionary-fill-start': {
    descriptionKey: 'dictionaryFill.startSubtitle',
    docsUrl: page('teacher/dictionary-fill'),
  },
  'dictionary-fill-run': {
    descriptionKey: 'dictionaryFill.runSubtitle',
    docsUrl: page('teacher/dictionary-fill'),
  },
  'dictionary-fill-run-student': {
    descriptionKey: 'dictionaryFill.runSubtitle',
    docsUrl: page('student/dictionary-fill'),
  },
  'lesson-tests-create': {
    descriptionKey: 'tests.lessonCreateSubtitle',
    docsUrl: page('teacher/tests#lesson-test-settings'),
  },
  notifications: {
    descriptionKey: 'notifications.subtitle',
    docsUrl: page('common/notifications-browser-push'),
  },
  'profile-notifications': {
    descriptionKey: 'notifications.preferencesHelpSubtitle',
    docsUrl: page('common/notifications-browser-push'),
  },
  accounting: {
    descriptionKey: 'accounting.subtitle',
    docsUrl: page('teacher/accounting'),
  },
  vacations: {
    descriptionKey: 'vacations.subtitle',
    docsUrl: page('teacher/vacations'),
  },
  'vacations-student': {
    descriptionKey: 'vacations.subtitle',
    docsUrl: page('student/vacations'),
  },
  help: {
    descriptionKey: 'help.subtitle',
    docsUrl: page('common/help-requests'),
  },
} as const

export function getPageHelpConfig(pageKey: PageHelpKey): PageHelpConfigItem {
  return PAGE_HELP_CONFIG[pageKey]
}
